import { readConsentCookie, removeConsentCookie, writeConsentCookie } from './cookie';
import { getIsGdprRegion } from './region';
import logger from './logger';
import type { ConsentHandler, ConsentSnapshot, ConsentStatus } from './types';

const computeSnapshot = (
  status: ConsentStatus,
  isGdprRegion: boolean,
  hydrated: boolean
): ConsentSnapshot => ({
  status,
  isGdprRegion,
  allowed: !isGdprRegion || status === 'accepted',
  hydrated,
});

// Initial snapshot is region-aware but consent-undecided. The cookie is only read once hydrate() runs on the client, which keeps the first client render identical to the server render (no hydration mismatch).
const initialIsGdprRegion = getIsGdprRegion();

// The cookie is the canonical persisted source of truth; this in-memory snapshot is a write-through mirror kept in sync by decide(). It exists because (1) the registry must push changes to handlers and (2) useSyncExternalStore requires a referentially stable snapshot. decide() is the only writer.
let snapshot: ConsentSnapshot = computeSnapshot('undecided', initialIsGdprRegion, false);

// Frozen reference returned for SSR/hydration; must stay referentially stable.
const serverSnapshot: ConsentSnapshot = computeSnapshot('undecided', initialIsGdprRegion, false);

// Flip to true to trace subscriber churn (subscribe/unsubscribe) and per-notify fan-out. Noisy: the subscriber set is dominated by useSyncExternalStore consumers (one per mounted useConsent caller), so leave off unless investigating leaks/churn.
const VERBOSE = false;
const CONSENT_CHANNEL_NAME = 'firsttable-consent';

const subscribers = new Set<ConsentHandler>();
let consentChannel: BroadcastChannel | null = null;

const notify = (): void => {
  logger.debug('notify', { snapshot, subscribers: subscribers.size });
  if (VERBOSE) {
    logger.debug(`notify: fanning out to ${subscribers.size} subscriber(s)`, snapshot);
  }
  subscribers.forEach((handler) => handler(snapshot));
};

const applyExternalDecision = (): void => {
  const isGdprRegion = getIsGdprRegion();
  const status = readConsentCookie();
  if (!isGdprRegion || (snapshot.hydrated && snapshot.status === status)) {
    return;
  }

  snapshot = computeSnapshot(status, isGdprRegion, true);
  logger.debug('cross-tab decision', snapshot);
  notify();
};

const broadcastDecision = (): void => {
  if (consentChannel) {
    consentChannel.postMessage(null);
  }
};

export const getConsent = (): ConsentSnapshot => snapshot;

export const getServerConsent = (): ConsentSnapshot => serverSnapshot;

export const subscribe = (handler: ConsentHandler): (() => void) => {
  subscribers.add(handler);
  if (VERBOSE) {
    logger.debug(`subscribe: +1 -> ${subscribers.size} subscriber(s)`);
  }
  return () => {
    subscribers.delete(handler);
    if (VERBOSE) {
      logger.debug(`unsubscribe: -1 -> ${subscribers.size} subscriber(s)`);
    }
  };
};

/**
 * Reads the persisted decision from the cookie and publishes the resulting snapshot. Called eagerly on client import (see bottom of file); idempotent so any later call is a safe no-op.
 * The cookie read only ever runs on the client, and `getServerConsent()` keeps the SSR/hydration render on the undecided snapshot so there is never a hydration mismatch.
 */
export const hydrate = (): void => {
  if (snapshot.hydrated) {
    return;
  }

  const isGdprRegion = getIsGdprRegion();

  if (!isGdprRegion) {
    // Off-GDPR there is no decision to persist; drop any lingering cookie unconditionally so stale/malformed values don't linger.
    removeConsentCookie();
    snapshot = computeSnapshot('undecided', false, true);
    logger.debug('hydrate: non-GDPR region, tracking allowed', snapshot);
    notify();
    return;
  }

  snapshot = computeSnapshot(readConsentCookie(), true, true);
  logger.debug('hydrate: GDPR region', snapshot);
  notify();
};

/** Persists a decision (write-through to the cookie) and notifies all subscribers when the status changes. */
export const decide = (status: ConsentStatus): void => {
  logger.debug('decide', { status });

  // Always (re)persist: re-confirming refreshes the cookie even if it was cleared mid-session.
  writeConsentCookie(status);
  broadcastDecision();

  // Skip the fan-out when the decision is unchanged — e.g. re-confirming the existing choice via the "Manage cookies" control — so we don't redundantly re-run every registered service handler's setup/teardown for a decision that didn't actually change.
  if (snapshot.hydrated && status === snapshot.status) {
    return;
  }

  snapshot = computeSnapshot(status, getIsGdprRegion(), true);
  notify();
};

// Hydrate the moment this (React-agnostic) module is first imported on the client, so the in-memory snapshot mirrors the cookie before any consumer — imperative (e.g. analytics queue gates) or React — reads it, rather than waiting on a component effect. SSR is skipped; the server keeps the undecided `serverSnapshot`.
if (typeof window !== 'undefined') {
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      consentChannel = new BroadcastChannel(CONSENT_CHANNEL_NAME);
      consentChannel.addEventListener('message', applyExternalDecision);
    } catch {
      consentChannel = null;
    }
  }

  window.addEventListener('focus', applyExternalDecision);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      applyExternalDecision();
    }
  });

  hydrate();
}
